import React, { useState } from 'react';
import { 
    Wand2, Sunrise, Coffee, Building2, TrendingUp, 
    Moon, Copy, CheckCircle, RefreshCw, MessageSquare 
} from 'lucide-react';
import { supabase } from '../supabaseClient'; // Adjust path if needed

const AIBroadcastGenerator = ({ onUseMessage }) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedOptions, setGeneratedOptions] = useState([]);
    const [activeSession, setActiveSession] = useState(null);
    const [copiedIndex, setCopiedIndex] = useState(null);

    const SESSION_TYPES = [
        { id: 'morning', label: 'Morning Greeting', icon: Sunrise, color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/20' },
        { id: 'london', label: 'London Open', icon: Coffee, color: 'text-blue-400', bg: 'bg-blue-400/10', border: 'border-blue-400/20' },
        { id: 'ny', label: 'NY Open', icon: Building2, color: 'text-indigo-400', bg: 'bg-indigo-400/10', border: 'border-indigo-400/20' },
        { id: 'wallstreet', label: 'US Stock Open', icon: TrendingUp, color: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/20' },
        { id: 'eod', label: 'End of Day', icon: Moon, color: 'text-purple-400', bg: 'bg-purple-400/10', border: 'border-purple-400/20' }
    ];

    const generateBroadcast = async (session) => {
        setIsGenerating(true);
        setActiveSession(session.id);
        setGeneratedOptions([]);

        // 🚨 THE KNOWMIA AI PROMPT (UPGRADED FOR RICH FORMATTING)
        const promptText = `
            Act as an elite, institutional-grade trading mentor leading a private community of traders. 
            Write 3 distinct, highly engaging broadcast messages for the: ${session.label}.
            
            CRITICAL FORMATTING RULES:
            1. Emphasize readability: You MUST use paragraphs (\n\n) to break up your text into digestible chunks.
            2. Use **bold** text to highlight key price levels, assets, or core concepts.
            3. Use appropriate emojis (📊, 🚀, 🛡️, 💡) to make the broadcast engaging.
            4. ALL 3 options MUST begin with this exact header on the first line: "🎙️ **${session.label.toUpperCase()}**\n\n"
            
            Guidelines for the 3 options:
            1. Option 1 should be highly analytical (focus on macro data, news, and technical levels).
            2. Option 2 should be motivational and psychology-driven (focus on discipline, patience, and mindset).
            3. Option 3 should be heavily focused on risk management (capital preservation, strict stops, and protecting the downside).
            
            Format your response STRICTLY as a JSON array of 3 strings. Example: ["message 1", "message 2", "message 3"]. Do not include markdown formatting outside the JSON array.
        `;

        try {
            // 🚨 THE FIX: Route to your own CRM Backend instead of Supabase Edge Functions!
            const response = await fetch('https://nommia-crm-backend-production.up.railway.app/api/ghostwriter/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prompt: promptText })
            });

            if (!response.ok) throw new Error("Backend AI Generation Failed");
            
            const data = await response.json();

            // Parse the AI's JSON response
            let options = [];
            try {
                // Look for data.text from our Node backend
                const rawAiText = data.text || "[]";
                
                // BULLETPROOF SCRAPER: Strip out markdown blocks, spaces, and invisible characters
                const cleanJson = rawAiText
                    .replace(/```json/gi, '')
                    .replace(/```/g, '')
                    .trim();
                    
                options = JSON.parse(cleanJson);
                
                // Failsafe: Ensure it's actually an array
                if (!Array.isArray(options)) {
                    options = [options.option1 || "Error", options.option2 || "Error", options.option3 || "Error"];
                }
                
            } catch (parseError) {
                console.error("AI returned malformed JSON. Raw Output:", data.text);
                throw new Error("AI formatting error. Please try again.");
            }

            setGeneratedOptions(options);

        } catch (error) {
            console.error("Failed to generate broadcast:", error);
            alert("Failed to generate AI broadcast. Check console.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    return (
        <div className="bg-[#111418] border border-white/5 rounded-2xl p-6 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6 border-b border-white/5 pb-4">
                <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center border border-yellow-500/20">
                    <Wand2 className="text-yellow-500" size={20} />
                </div>
                <div>
                    <h3 className="text-xl font-bold text-white">Knowmia Broadcast Assistant</h3>
                    <p className="text-xs text-slate-400">Instantly generate high-quality, contextual broadcasts for your community.</p>
                </div>
            </div>

            {/* QUICK ACTION BUTTONS */}
            <div className="flex flex-wrap gap-3 mb-8">
                {SESSION_TYPES.map((session) => {
                    const Icon = session.icon;
                    const isActive = activeSession === session.id;
                    return (
                        <button
                            key={session.id}
                            onClick={() => generateBroadcast(session)}
                            disabled={isGenerating}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                                isActive && isGenerating 
                                ? `${session.bg} ${session.border} animate-pulse cursor-wait` 
                                : `bg-slate-900 border-white/5 hover:${session.bg} hover:${session.border}`
                            }`}
                        >
                            <Icon size={16} className={session.color} />
                            <span className={`text-sm font-bold ${isActive && isGenerating ? 'text-white' : 'text-slate-300'}`}>
                                {session.label}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* RESULTS AREA */}
            {isGenerating && (
                <div className="flex flex-col items-center justify-center py-12 border border-dashed border-white/10 rounded-xl bg-slate-900/50">
                    <RefreshCw className="text-yellow-500 animate-spin mb-4" size={32} />
                    <p className="text-slate-400 font-medium animate-pulse">Knowmia is analyzing market conditions and drafting broadcasts...</p>
                </div>
            )}

            {!isGenerating && generatedOptions.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {generatedOptions.map((msg, index) => (
                        <div key={index} className="bg-slate-900 border border-white/5 rounded-xl p-5 flex flex-col relative group hover:border-yellow-500/30 transition-colors">
                            <span className="absolute -top-3 left-4 bg-slate-800 border border-white/10 text-[10px] uppercase font-bold text-yellow-500 px-3 py-1 rounded-full shadow-lg">
                                {index === 0 ? 'Macro & Analytical' : index === 1 ? 'Psychology & Motivation' : 'Risk & Protection'}
                            </span>
                            
                            <p className="text-sm text-slate-300 leading-relaxed mt-2 flex-1 whitespace-pre-wrap">
                                {msg}
                            </p>

                            <div className="flex gap-2 mt-6 pt-4 border-t border-white/5">
                                <button 
                                    onClick={() => handleCopy(msg, index)}
                                    className="flex-1 flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 py-2 rounded-lg text-xs font-bold transition-colors"
                                >
                                    {copiedIndex === index ? <CheckCircle size={14} className="text-emerald-500" /> : <Copy size={14} />}
                                    {copiedIndex === index ? 'Copied!' : 'Copy Text'}
                                </button>
                                {onUseMessage && (
                                    <button 
                                        onClick={() => onUseMessage(msg)}
                                        className="flex-1 flex items-center justify-center gap-2 bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-500 border border-yellow-500/20 py-2 rounded-lg text-xs font-bold transition-colors"
                                    >
                                        <MessageSquare size={14} /> Send to Input
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AIBroadcastGenerator;